import React, { Fragment } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useFetch, useLanguage, usePagination } from "hooks";
import { GridList, Layout, MultimediaCard, Skeletons, Icons } from "components";
import Tabs from "./Tabs";
import { useNavigate } from "react-router-dom";
import ClickableSquare from "./ClickableSquare";
import { STORAGE_URL } from "constants";
import { useSelector } from "react-redux";
import { ArrowLeft, ArrowRight } from "react-bootstrap-icons";

/* 
if the user programs in user data then:
if it's one select it and prevents the user to select
if it's more then one or then let user to select between this ids by filtering programs on user ids 
if the user programs in user data empty array or there is no data then the user will select from all

class and subject in the same way
*/

export default function List() {
  const language = useLanguage(); //language returned from useLanguage hook; it’s an object with current language key --> "value in current language";
  const navigate = useNavigate();
  const { page, handleSetPage } = usePagination();
  const { user } = useSelector((state) => state.auth); // call data from auth store
  const [booksResponse, booksRequest] = useFetch(`getBooks`, "POST");
  const [_, handleGetPrograms] = useFetch(`getPrograms`, "GET");
  const [booksFilterResponse, booksFilterRequest] = useFetch(
    `getBooksFilters`,
    "POST"
  );

  const [programs, setPrograms] = React.useState([]);
  const [programId, setProgramId] = React.useState(null);

  const [subjects, setSubjects] = React.useState([]);
  const [subjectId, setSubjectId] = React.useState(null);

  const [classes, setClasses] = React.useState([]);
  const [classId, setClassId] = React.useState(null);

  // const setProgramsHandler = React.useCallback(
  //   ({ data: programs }) => {
  //     let filteredPrograms;
  //     if (user?.user_programs && user?.user_programs.length > 0)
  //       filteredPrograms = programs.filter(({ id }) =>
  //         user?.user_programs.includes(id)
  //       );
  //     else if (user?.role == 3) filteredPrograms = programs;
  //     setPrograms(filteredPrograms);
  //     if (filteredPrograms?.length === 1) setProgramId(filteredPrograms[0]?.id);
  //   },
  //   [user?.user_programs]
  // ); // after receiving programs then set it to programs after filtering it on user user_programs
  
  const setProgramsHandler = React.useCallback(
    ({ data: programs }) => {
      let filteredPrograms;
      // if (user?.user_programs && user?.user_programs.length > 0)
      if (user?.program_id)
        filteredPrograms = programs.filter(({ id }) =>
          user?.program_id === id
        );
      else if (user?.role == 3) filteredPrograms = programs;
      setPrograms(filteredPrograms);
      if (filteredPrograms?.length === 1) setProgramId(filteredPrograms[0]?.id);
    },
    [user?.program_id]
  );

  const handleGetData = React.useCallback(async () => {
    handleSetPage(1);
    if (programId && subjectId && classId)
      booksRequest(
        {
          program_id: programId,
          filters: {
            subjects: [subjectId],
            classes: [classId],
          },
        },
        `?page=${page}`
      );
  }, [booksRequest, classId, handleSetPage, page, programId, subjectId]); // on any of programId, subjectId and classId change re call the data

  const goBack = React.useCallback(
    (current) => {
      if (current === "class") {
        if (programs?.length > 1) setProgramId(null);
      }
      if (current === "subject") {
        if (classes?.values?.length > 1) setClassId(null);
        else if (programs?.length > 1) setProgramId(null);
      }
    },
    [programs, classes]
  ); // delete the last value by taking the current step

  const isGoBack = React.useCallback(
    (current) => {
      if (current === "class") {
        return programs?.length > 1 ? true : false;
      }

      if (current === "subject") {
        return programs?.length > 1 || classes?.values?.length > 1
          ? true
          : false;
      }
    },
    [programs, classes]
  ); // to hiding go back button if there is no back data

  React.useEffect(() => {
    const usersPrograms = user?.user_programs;
    const usersClass = user?.user_classes;

    if (usersPrograms?.length === 1) setProgramId(usersPrograms[0]);
    if (usersClass?.length === 1) setClassId(usersClass[0]);
  }, [user]); // set program and class with user data

  React.useEffect(() => {
    handleGetPrograms(null, null, setProgramsHandler); // send setProgramsHandler as on success function
  }, [handleGetPrograms, setProgramsHandler]);

  React.useEffect(() => {
    handleGetData();
  }, [handleGetData]);

  React.useEffect(() => {
    handleSetPage(1);
    if (programId) {
      booksFilterRequest({
        page_type: "curriculum",
        program_id: programId,
        filters: {
          subjects: subjectId ? [subjectId] : [],
          classes: classId ? [classId] : [],
        },
      }); // call filters data
    }
  }, [booksFilterRequest, classId, handleSetPage, programId, subjectId]);

  React.useEffect(() => {
    if (booksFilterResponse.result.data) {
      const subjects = booksFilterResponse.result.data.filter(
        (item) => item.key === "subjects"
      );
      setSubjects(subjects[0]);
    }
    if (booksFilterResponse.result.data) {
      const classes = booksFilterResponse.result.data.filter(
        (item) => item.key === "classes"
      );
      if (user?.user_classes && user?.user_classes.length > 0) {
        const values = classes[0]?.values?.filter(({ id }) =>
          user?.user_classes.includes(id)
        );
        setClasses({
          ...classes[0],
          values: values,
        });

        if (values?.length === 1 && classId) setClassId(values[0]?.id);
      } else {
        setClasses(classes[0]);
        if (classes[0]?.values?.length === 1 && classId)
          setClassId(classes[0]?.values[0]?.id);
      }
    }
  }, [booksFilterResponse.result.data, user?.user_classes, classId]); // set subjects and classes with fitter response data

  return (
    <Layout>
      <div id="curriculum_page" className="page_container pt-5">
        <section className="mb-3 page_top">
          <Container>
            <Row>
              <Col xs={12} className="mb-4">
                <h1 className="fw-medium text-primary text-center">
                  {language.curriculumSection}
                </h1>
              </Col>
              <Col xs={12} className="mb-4">
                <Tabs
                  tabs={[
                    {
                      id: 1,
                      name: language.books,
                    },
                    {
                      id: 2,
                      name: language.curriculumSection,
                    },
                  ]}
                  currentTab={2}
                  setCurrentTab={() => {
                    navigate("/books");
                  }} // if the user clicks on book tab
                  alternate
                />
              </Col>
            </Row>
          </Container>
        </section>

        <section className="mb-3">
          <Container>
            <Row>
              <Col xs={12} className="mx-auto">
                {!booksFilterResponse.loading &&
                  (!programId ? ( // if no programId you should to select one
                    programs?.length > 0 ? (
                      <ClickableSquare
                        tabs={programs}
                        currentTab={programId}
                        setCurrentTab={setProgramId}
                        alternate
                      />
                    ) : (
                      <>
                        <p>{language.noData}</p>
                      </>
                    )
                  ) : !classId &&
                    !(
                      classes.values.length === 0 &&
                      user?.user_classes?.length > 0 &&
                      subjects.values.length === 0
                    ) ? ( // if no classId you should to select one
                    <>
                      {isGoBack("class") && (
                        <div>
                          <Button
                            variant="text"
                            onClick={() => goBack("class")}
                            className="text-primary p-0"
                            style={{ top: -16 }}
                          >
                            {language.direction === "ltr" ? (
                              <ArrowLeft size={30} />
                            ) : (
                              <ArrowRight size={30} />
                            )}
                          </Button>
                        </div>
                      )}

                      <h3 className="text-primary mb-2">{language.classes}</h3>

                      {classes?.values?.length > 0 ? (
                        <ClickableSquare
                          tabs={
                            classes?.values && classes.values.length > 0
                              ? classes?.values
                              : []
                          }
                          currentTab={classId}
                          setCurrentTab={setClassId}
                          alternate
                        />
                      ) : (
                        <>
                          <p>{language.noClasses}</p>
                        </>
                      )}
                    </>
                  ) : !subjectId ? ( // if no subjectId you should to select one
                    <>
                      {isGoBack("subject") && (
                        <div>
                          <Button
                            variant="text"
                            onClick={() => goBack("subject")}
                            className="text-primary p-0"
                            style={{ top: -16 }}
                          >
                            {language.direction === "ltr" ? (
                              <ArrowLeft size={30} />
                            ) : (
                              <ArrowRight size={30} />
                            )}
                          </Button>
                        </div>
                      )}
                      <h3 className="text-primary mb-2">{language.subjects}</h3>
                      {subjects?.values?.length > 0 ? (
                        <ClickableSquare
                          tabs={
                            subjects?.values && subjects.values.length > 0
                              ? subjects?.values
                              : []
                          }
                          currentTab={subjectId}
                          setCurrentTab={setSubjectId}
                          alternate
                        />
                      ) : (
                        <>
                          <p>{language.noSubjects}</p>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <Button
                          variant="text"
                          onClick={() => {
                            setSubjectId(null);
                          }}
                          className="text-primary p-0"
                          style={{ top: -16 }}
                        >
                          {language.direction === "ltr" ? (
                            <ArrowLeft size={30} />
                          ) : (
                            <ArrowRight size={30} />
                          )}
                        </Button>
                      </div>
                      <h3 className="text-primary mb-3">
                        {language.schoolBook}
                      </h3>
                      {!booksResponse.loading && booksResponse.result.data ? (
                        <GridList
                          perRow={4}
                          itemsCount={booksResponse.result.meta.total}
                          perPage={booksResponse.result.meta.per_page}
                          as={MultimediaCard}
                          items={booksResponse.result.data}
                        />
                      ) : (
                        <Skeletons.CardsSkeleton count={12} perRow={4} />
                      )}
                    </>
                  ))}

                {!booksFilterResponse.loading &&
                  typeof booksFilterResponse.result.platforms === "object" &&
                  booksFilterResponse.result.platforms.values &&
                  booksFilterResponse.result.platforms.values.length > 0 && (
                    <Fragment>
                      <hr />
                      <h3 className="text-primary mb-3">
                        {language.platforms}
                      </h3>
                      <ul className="list-unstyled gap-4 d-flex flex-wrap">
                        {[...booksFilterResponse.result.platforms.values].map(
                          (item, idx) => (
                            <li key={item.id} className="m-0">
                              <div className="px-3 py-2 bg-primary d-inline-block rounded-3">
                                <Row>
                                  <Col xs={12} className="text-center">
                                    <div className="d-flex justify-content-center align-items-center">
                                      <div className="platform-img-container d-flex justify-content-center align-items-center">
                                        {item.icon ? (
                                          <img
                                            src={`${STORAGE_URL}${item.icon}`}
                                            alt={item.name}
                                          />
                                        ) : (
                                          <Icons.BookMark
                                            width={60}
                                            height={60}
                                            fill="white"
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </Col>
                                  <Col xs={12} className="text-center">
                                    <a
                                      target="_blank"
                                      rel="noreferrer"
                                      href={item.link}
                                      className="text-white h6"
                                      style={{
                                        bottom: 3,
                                      }}
                                    >
                                      {item.name}
                                    </a>
                                  </Col>
                                </Row>
                              </div>
                            </li>
                          )
                        )}
                      </ul>
                    </Fragment>
                  )}
              </Col>
            </Row>
          </Container>
        </section>
      </div>
    </Layout>
  );
}
